import React, { useMemo, useRef } from 'react';

import { ApiObject, Label, Program, School } from 'app/types';
import Text from 'common/components/Text/Text';

import styles from 'features/explore/school/SchoolDetail.module.scss';
import { useMediaQuery } from 'react-responsive';
import { TagIcon } from 'assets/explore';
import { entityIsProgram, entityIsSchool } from 'common/utils';

interface CompProps {
  entity?: School | Program;
  comesFromExplore?: boolean;
}

interface TagCardProps {
  title?: string;
  description?: string;
}

const TagCard = ({ title, description }: TagCardProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  return (
    <div
      className={`card h-100 ${styles.tagContainer}`}
      style={isMobile ? { zIndex: '4' } : { zIndex: 'auto' }}
    >
      <img src={TagIcon} className={styles.tagIcon}></img>
      <Text size={isMobile ? 14 : 18} weight={600} inlineStyle={{ marginBottom: '4px' }}>
        {title}
      </Text>
      <Text size={14} weight={400}>
        {description}
      </Text>
    </div>
  );
};

export const TagsSection = ({ entity, comesFromExplore }: CompProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const refScroll = useRef<any>();

  const scroll = (scrollOffset: number) => {
    refScroll.current ? (refScroll.current.scrollLeft += scrollOffset) : '';
  };

  const entityHasLabels = useMemo(() => entity?.labels && entity?.labels.length > 0, [entity]);

  return (
    <div className={styles.sectionContainerAux}>
      {!!(
        entityHasLabels ||
        (entityIsProgram(entity) && entity?.school.labels && entity?.school.labels.length > 0)
      ) && (
        <>
          <div className={styles.titleContainer}>
            {comesFromExplore ? (
              <h4 className={styles.titleText}>
                {entityIsProgram(entity)
                  ? `Why study ${entity?.name} at ${entity?.school.name}`
                  : `Why study at ${entity?.name}`}
              </h4>
            ) : (
              <h3 className={styles.titleText}>
                {entityIsProgram(entity)
                  ? `Why study ${entity?.name} at ${entity?.school.name}`
                  : `Why study at ${entity?.name}`}
              </h3>
            )}
            {isMobile && (
              <>
                <div className={styles.arrowContainer} onClick={() => scroll(-150)}>
                  <i className="bi bi-chevron-compact-left"></i>
                </div>
                <div className={styles.arrowContainer} onClick={() => scroll(150)}>
                  <i className="bi bi-chevron-compact-right"></i>
                </div>
              </>
            )}
          </div>
          {isMobile ? (
            <div
              className={`${styles.tagsContainer} row flex-row flex-nowrap mt-3 pt-1`}
              ref={refScroll}
            >
              {entityHasLabels
                ? entity?.labels.map((label: Label) => (
                    <div className={styles.tag} key={label.id}>
                      <TagCard
                        title={label.tag.name}
                        description={label.description ? label.description : label.tag.description}
                      />
                    </div>
                  ))
                : entityIsProgram(entity) &&
                  entity?.school.labels.map((label: Label) => (
                    <div className={styles.tag} key={label.id}>
                      <TagCard
                        title={label.tag.name}
                        description={label.description ? label.description : label.tag.description}
                      />
                    </div>
                  ))}
            </div>
          ) : (
            <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 mt-5">
              {entityIsSchool(entity) && entityHasLabels
                ? entity?.labels.map((label: Label) => (
                    <div className={styles.tagDesktop} key={label.id}>
                      <TagCard
                        title={label.tag.name}
                        description={label.description ? label.description : label.tag.description}
                      />
                    </div>
                  ))
                : !!(
                    entityIsProgram(entity) &&
                    (entityHasLabels ||
                      (entity?.school.labels.length && entity?.school.labels.length > 0))
                  ) && (
                    <>
                      {entity?.labels.slice(0, 8).map((label: Label) => (
                        <div className={styles.tagDesktop} key={label.id}>
                          <TagCard
                            title={label.tag.name}
                            description={
                              label.description ? label.description : label.tag.description
                            }
                          />
                        </div>
                      ))}
                      {entity?.school.labels
                        .slice(0, 8 - entity.labels.length)
                        .map((label: Label) => (
                          <div className={styles.tagDesktop} key={label.id}>
                            <TagCard
                              title={label.tag.name}
                              description={
                                label.description ? label.description : label.tag.description
                              }
                            />
                          </div>
                        ))}
                    </>
                  )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
